import React from 'react';
import styles from './NoteCard.module.css';
import { HeartIcon, LoaderIcon } from '../../../../components/Icons';

/** 将 **bold** 渲染为 <strong>，保留换行 */
const formatRichText = (text) => {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const elements = [];
  lines.forEach((line, lineIdx) => {
    if (lineIdx > 0) elements.push(<br key={`br-${lineIdx}`} />);
    const segments = line.split(/(\*\*[\s\S]*?\*\*)/g).filter(Boolean);
    segments.forEach((seg, segIdx) => {
      if (seg.startsWith('**') && seg.endsWith('**')) {
        elements.push(
          <strong key={`b-${lineIdx}-${segIdx}`} className={styles.richBold}>
            {seg.slice(2, -2)}
          </strong>
        );
      } else {
        elements.push(<React.Fragment key={`t-${lineIdx}-${segIdx}`}>{seg}</React.Fragment>);
      }
    });
  });
  return elements;
};

/**
 * 单条笔记条目（用于 NoteGroupCard 内部）
 */
const NoteEntry = ({
  noteItem,
  groupKey,
  isAiNote,
  aiPhrases = [],
  favoriteLoading = false,
  onFavoriteToggle,
}) => {
  const noteId = noteItem.id ?? noteItem.favorite_id ?? noteItem.favoriteId;
  const isAiNoteEntry = isAiNote && (noteItem.note_content === '-1' || noteItem.content === '-1');
  const createdAt = noteItem.createdAt || noteItem.created_at;
  const rawContent = noteItem.note_content ?? noteItem.content ?? '';

  return (
    <div className={styles.noteEntry}>
      <div className={styles.noteEntryHeader}>
        {createdAt && (
          <time className={styles.meta}>
            {new Date(createdAt).toLocaleString()}
          </time>
        )}
        <button
          type="button"
          className={styles.favoriteBtn}
          onClick={() => onFavoriteToggle?.(noteItem)}
          disabled={favoriteLoading}
          aria-label="取消收藏"
          title="取消收藏"
        >
          {favoriteLoading ? (
            <LoaderIcon size={16} />
          ) : (
            <HeartIcon size={20} filled />
          )}
        </button>
      </div>
      {isAiNoteEntry && Array.isArray(aiPhrases) && aiPhrases.length > 0 ? (
        <div className={styles.aiNotePhraseList}>
          {aiPhrases.map((phrase, phraseIdx) => {
            if (!phrase || typeof phrase !== 'object') return null;
            return (
              <div key={`${groupKey}-${noteId}-phrase-${phraseIdx}`} className={styles.aiNotePhraseItem}>
                <div className={styles.aiNotePhraseHeader}>
                  <span className={styles.aiNotePhraseTitle}>
                    {phrase.phrase || '短语'}
                  </span>
                  {phrase.meaning && (
                    <span className={styles.aiNotePhraseMeaning}>
                      {phrase.meaning}
                    </span>
                  )}
                </div>
                {phrase.usage_note && (
                  <p className={styles.aiNotePhraseUsage}>
                    <strong>用法：</strong>
                    {phrase.usage_note}
                  </p>
                )}
                {(phrase.example_en || phrase.example_cn) && (
                  <div className={styles.aiNotePhraseExampleBox}>
                    {phrase.example_en && (
                      <p className={styles.aiNotePhraseExample}>
                        {phrase.example_en}
                      </p>
                    )}
                    {phrase.example_cn && (
                      <p className={styles.aiNotePhraseExampleCn}>{phrase.example_cn}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : !isAiNoteEntry && rawContent ? (
        <div className={styles.noteContent}>
          {formatRichText(rawContent)}
        </div>
      ) : null}
    </div>
  );
};

/**
 * 共享笔记卡片组件 — 按句子分组显示笔记
 * 用于 ChapterLearn 和 BookNotesListPage
 *
 * @param {object}   group              - 分组数据 { key, sentenceId, originalText, translatedText, notes, isAiNote, aiPhrases }
 * @param {boolean}  showChinese        - 是否显示中文原文
 * @param {boolean}  showEnglish        - 是否显示英文翻译
 * @param {object}   favoriteLoadingMap - { [noteId]: boolean }
 * @param {function} onFavoriteToggle   - (noteItem) => void
 * @param {object}   playingClip        - { en: sentenceId|null }
 * @param {function} onPlayClip         - (fakeSentence, lang) => void
 * @param {function} onStopClip         - (lang) => void
 * @param {number}   chapterId          - 当前章节 ID
 * @param {object}   sentenceTimings    - { en: { [sentenceId]: { start, end } } }
 */
const NoteGroupCard = ({
  group,
  showChinese = true,
  showEnglish = true,
  favoriteLoadingMap = {},
  onFavoriteToggle,
  playingClip = {},
  onPlayClip,
  onStopClip,
  chapterId,
  sentenceTimings = { en: {} },
}) => {
  if (!group) return null;

  const sentenceLabel =
    Number.isFinite(group.sentenceId) && group.sentenceId !== null
      ? group.sentenceId
      : '未知';

  const enTiming = Number.isFinite(group.sentenceId)
    ? sentenceTimings?.en?.[group.sentenceId] ?? null
    : null;
  const hasEnClip = enTiming && enTiming.start != null && chapterId;
  const fakeSentenceId = `note-${group.sentenceId}`;
  const isEnPlaying = playingClip?.en === fakeSentenceId;

  const handlePlayEnglish = () => {
    if (!hasEnClip || !onPlayClip) return;
    if (isEnPlaying) {
      onStopClip?.('en');
      return;
    }
    const fakeSentence = {
      id: fakeSentenceId,
      chapter_id: chapterId,
      en_start_timestamp: enTiming.start,
      en_end_timestamp: enTiming.end,
    };
    onPlayClip(fakeSentence, 'en');
  };

  return (
    <article className={styles.card}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.label}>SENTENCE {sentenceLabel}</span>
          {hasEnClip && (
            <button
              type="button"
              className={`${styles.audioBtn} ${isEnPlaying ? styles.audioBtnActive : ''}`}
              onClick={handlePlayEnglish}
              title="播放英文发音"
              aria-label="播放英文发音"
            >
              <span className="material-symbols-rounded">volume_up</span>
            </button>
          )}
        </div>
        {group.notes.length > 1 && (
          <span className={styles.countBadge}>
            {group.notes.length} 条笔记
          </span>
        )}
      </header>

      {(showChinese || showEnglish) && (group.originalText || group.translatedText) && (
        <div className={styles.sentences}>
          {showChinese && group.originalText && (
            <div className={styles.originalText} title="句子中文">
              {group.originalText}
            </div>
          )}
          {showEnglish && group.translatedText && (
            <div className={styles.translatedText} title="句子英文">
              {group.translatedText}
            </div>
          )}
        </div>
      )}

      <div className={styles.noteEntries}>
        {group.notes.map((noteItem) => {
          const noteId = noteItem.id ?? noteItem.favorite_id ?? noteItem.favoriteId;
          return (
            <NoteEntry
              key={`${group.key}-${noteId}`}
              noteItem={noteItem}
              groupKey={group.key}
              isAiNote={group.isAiNote}
              aiPhrases={group.aiPhrases}
              favoriteLoading={!!favoriteLoadingMap[noteId]}
              onFavoriteToggle={onFavoriteToggle}
            />
          );
        })}
      </div>
    </article>
  );
};

export { NoteEntry };
export default NoteGroupCard;

